import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { getActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/easi-fast-check-in-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  BaseModuleProps,
  EasiFastCheckInModuleSettings,
} from "../../types/index.js";
import {
  getActiveColorScheme,
  getSupportedLanguage,
} from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";

type Props = BaseModuleProps<EasiFastCheckInModuleSettings>;

const EasiFastCheckInModule: FunctionComponent<Props> = ({
  translatedModule: {
    id,
    settings: { textAlign, url },
    translation: {
      languageId,
      settings: { title, subtitle },
    },
  },
  translatedModule,
  isFirstOnPage,
  isActive,
  isPreview,
}) => {
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(
      state.colorSchemes,
      getActiveSite(state.sites),
      translatedModule,
    ),
  );
  const lang = getSupportedLanguage(languageId, ["de", "it", "en"]);
  const queryString =
    typeof location !== "undefined" ? `?parameter=${location.href}` : "";
  const urlWithLang = `${url}/lang/${lang}${queryString}`;

  const ref = useRef<HTMLDivElement>(null);
  const [iframeHeight, setIframeHeight] = useState(0);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!ref.current) return;
      if (typeof event.data === "string") {
        const matches = event.data.match(/^fciResizeIframe:([0-9]+)/);
        const newSize = Number(matches?.[1]);
        newSize &&
          setIframeHeight((lastSize) =>
            newSize !== lastSize ? newSize : lastSize,
          );
      }

      if (event.data?.task === "scroll_top") {
        window.scrollTo(0, ref.current.offsetTop);
      }
    };

    window.addEventListener("message", onMessage, false);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, []);

  return (
    <ModuleWithHeadings
      title={title}
      subtitle={subtitle}
      id={id}
      className={styles.EasiFastCheckInModule}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
    >
      <div className={moduleBaseStyles.Module__Wrapper}>
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
      </div>

      <div ref={ref} className={moduleBaseStyles.Module__Wrapper}>
        {!url && (
          <div className={styles.EasiFastCheckInModule__LogoContainer}>
            <img
              className={styles.EasiFastCheckInModule__Logo}
              src="/easi-fast-check-in.svg"
              width="253"
              height="45"
              alt="easi FastCheckIn"
              loading="lazy"
            />
          </div>
        )}
        {url && (
          <iframe
            scrolling="no"
            name="Fast+Check-in"
            style={{ height: iframeHeight || undefined }}
            className={ClassNames(styles.EasiFastCheckInModule__Iframe, {
              [styles["EasiFastCheckInModule__Iframe--no-click"]]:
                !isActive && isPreview,
            })}
            src={urlWithLang}
            loading="lazy"
          />
        )}
      </div>
    </ModuleWithHeadings>
  );
};

export default EasiFastCheckInModule;
